import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Truss } from '@paragon/contract-library';
import { BackgroundOptimizationOperation, OptimizationCostComparison } from '@paragon/data-connections';
import { allComponentDesignsUnloaded } from '@paragon/layout-models';
import { assertExhaustiveSwitch } from '@paragon/utilities';

/**
 * How far along the run is, as the server last reported it. `numCombinations` is how many designs the search will
 * consider and `currentIteration` how many it has reached; either can still be null early on, before the optimizer has
 * worked out the size of its search space.
 */
export interface OptimizationProgress {
  readonly numCombinations: number | null;
  readonly currentIteration: number | null;
}

/**
 * The optimization run the panel is reporting on. `starting` covers the request that hands the run to the queue, before
 * there is an operation to poll; the rest mirror the server's statuses.
 */
export interface OptimizationRunState {
  readonly componentDesignGuid: string;
  readonly componentName: string;
  readonly status: 'starting' | 'pending' | 'inProgress' | 'completed' | 'failed';
  readonly progress: OptimizationProgress;
  /** The best truss the run has found so far, and once it completes, the one it settled on. */
  readonly bestTrussSoFar: Truss | null;
  /** What `bestTrussSoFar` costs against the design the run started from, or null while the run cannot price them. */
  readonly costComparison: OptimizationCostComparison | null;
  /** Whether the user has taken the finished run's truss onto the component design; nothing is applied until they do. */
  readonly adopted: boolean;
}

export interface OptimizationProgressState {
  /**
   * Only the most recent run is kept: a new one replaces it.
   */
  readonly latestRun: OptimizationRunState | null;
}

const initialState: OptimizationProgressState = {
  latestRun: null,
};

const optimizationProgressSlice = createSlice({
  name: 'optimizationProgress',
  initialState,
  reducers: {
    optimizationRunStarted: (state, action: PayloadAction<{ componentDesignGuid: string; componentName: string }>) => {
      state.latestRun = {
        componentDesignGuid: action.payload.componentDesignGuid,
        componentName: action.payload.componentName,
        status: 'starting',
        progress: { numCombinations: null, currentIteration: null },
        bestTrussSoFar: null,
        costComparison: null,
        adopted: false,
      };
    },

    optimizationRunPolled: (
      state,
      action: PayloadAction<{ componentDesignGuid: string; operation: BackgroundOptimizationOperation }>,
    ) => {
      const { componentDesignGuid, operation } = action.payload;
      // A poll that lands after the run it belongs to was replaced has nothing left to report on.
      if (state.latestRun?.componentDesignGuid !== componentDesignGuid) return;

      state.latestRun.status = mapOperationStatus(operation.status);
      state.latestRun.progress = {
        numCombinations: operation.numCombinations,
        currentIteration: operation.currentIteration,
      };
      // The truss is null until the search finds its first candidate; hold on to the last one it reported rather than
      // dropping the preview back to nothing. Its cost is taken from the same poll, never carried over on its own: a
      // truss shown beside an earlier candidate's price would misreport the saving.
      if (operation.truss != null) {
        state.latestRun.bestTrussSoFar = castDraft(operation.truss);
        state.latestRun.costComparison = operation.costComparison;
      }
    },

    optimizedDesignAdopted: (state, action: PayloadAction<{ componentDesignGuid: string }>) => {
      if (state.latestRun?.componentDesignGuid !== action.payload.componentDesignGuid) return;

      state.latestRun.adopted = true;
    },

    optimizationRunFailed: (state, action: PayloadAction<{ componentDesignGuid: string; errorMessage: string }>) => {
      if (state.latestRun?.componentDesignGuid !== action.payload.componentDesignGuid) return;

      state.latestRun.status = 'failed';
    },

    optimizationRunDismissed: (state) => {
      state.latestRun = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(allComponentDesignsUnloaded, () => initialState);
  },
});

function mapOperationStatus(status: BackgroundOptimizationOperation['status']): OptimizationRunState['status'] {
  switch (status) {
    case 'Pending':
      return 'pending';
    case 'InProgress':
      return 'inProgress';
    case 'Completed':
      return 'completed';
    case 'Failed':
      return 'failed';
    default:
      assertExhaustiveSwitch(status);
  }
}

export const {
  optimizationRunStarted,
  optimizationRunPolled,
  optimizedDesignAdopted,
  optimizationRunFailed,
  optimizationRunDismissed,
} = optimizationProgressSlice.actions;
export const optimizationProgress = optimizationProgressSlice.reducer;
